import React, { useMemo, useCallback, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import cn from 'classnames'
import range from 'lodash/range'
import {
  getPages,
  getPagesTotal,
  isPagesBusy,
  isPagesLoaded,
  getPagesQueryStringArguments
} from '../../redux/slices/content'
import { pagesLoadMoreActionCreator } from '../../redux/slices/content/pages'

import { PAGE_SIZE } from '../../redux/middlewares/content/pages'
import List from '../List'
import Card from '../List/Card'
import { resolveInternalLink } from '../../helpers/resolveLink'
import { createUseStyles } from 'react-jss'
import theme from '../../style/theme'
import { vw } from '../../style/vw'
import { span, sliceMarginSpan } from '../../style/span'
import ColorContext from '../ColorContext'
import SearchBarWithPrepTimeFilter from '../SearchBarWithPrepTimeFilter'
// import { text } from 'body-parser'

const PageGridSlice = ({ className }) => {
  const borderColor = useContext(ColorContext)
  const classes = useStyles({ borderColor })
  const pages = useSelector(getPages)
  const total = useSelector(getPagesTotal)
  const loaded = useSelector(isPagesLoaded)
  const { page: index } = useSelector(getPagesQueryStringArguments)
  const busy = useSelector(isPagesBusy)
  const dispatch = useDispatch()
  // State to track filter results
  const [filterState, setFilterState] = React.useState({
    hasResults: true,
    isFiltering: false,
    resultCount: 0
  })
  const loadMore = useCallback(() => {
    dispatch(pagesLoadMoreActionCreator())
  }, [])

  const handleFilterChange = useCallback((newFilterState) => {
    setFilterState(newFilterState)
  }, [])

  const pageCards = useMemo(() => {
    const expectedCards = PAGE_SIZE * (index + 1)
    var cards = []
    if (pages) {
      cards = pages.map((page, i) => {
        const { id, title, image, subtitle, description, mealPrepTime } = page
        return {
          id, title, image, subtitle, description, borderColor, mealPrepTime, link: resolveInternalLink(page)
        }
      })
    }
    // if the pages have not loaded or it is busy getting the pages, then show the placeholders
    if (busy || !loaded) {
      const currentPageLength = (pages ? pages.length : 0)
      cards = [
        ...cards,
        ...range(expectedCards - currentPageLength).map((x, i) => ({ placeholder: true }))
      ]
    }
    return cards
  }, [index, pages, loaded, busy])

  const hasMore = !pages || total > pages.length
  const showLoadMore = hasMore && (!filterState.isFiltering || filterState.hasResults)
  return (
    <List className={classes.list}>
      <SearchBarWithPrepTimeFilter
        placeholder={<><span className={classes.bold}>Search by</span> Cut, Flavour or Craving</>}
        onFilterChange={handleFilterChange}
      />
      {/* No results message */}
      {filterState.isFiltering && !filterState.hasResults && (
        <div className={classes.noResults}>
          <h3 className={classes.noResultsTitle}>No Results Found</h3>
          <p className={classes.noResultsText}>
            Try adjusting your search terms or prep time filter to find what you're looking for.
          </p>
        </div>
      )}
      <div className={classes.gridWrapper}>
        {pageCards.map(({ id, title, image, subtitle, description, link, borderColor, placeholder, mealPrepTime }, i) => (
          <Card
            key={id || i}
            id={id}
            title={title}
            image={image}
            subtitle={subtitle}
            description={description}
            link={link}
            borderColor={borderColor}
            placeholder={placeholder}
            mealPrepTime={mealPrepTime}
            className={cn({ [classes.lastCard]: showLoadMore && i === pageCards.length - 1 })}
          />
        ))}
      </div>
      {showLoadMore && (
        <div className={classes.footer}>
          <button disabled={busy} className={classes.link} onClick={loadMore}>Load More</button>
        </div>
      )}
    </List>
  )
}

const useStyles = createUseStyles({
  list: {
    margin: 0,
    [theme.breakpoints.up('md')]: {
      margin: 0
    }
  },
  gridWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: span(1),
    [theme.breakpoints.up('md')]: {
      gap: '70px 20px'
    },
    '@media (max-width: 1500px)': {
      gridTemplateColumns: '1fr 1fr 1fr',
      maxWidth: 'calc(100% - 7rem)',
      gap: '50px 20px'
    },
    '@media (max-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      maxWidth: 'calc(100% - 7rem)',
      gap: '50px 20px'
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
      maxWidth: 'calc(100% - 3.5rem)'
    },
    maxWidth: 'calc(100% - 9rem)',
    margin: 'auto'
  },
  footer: {
    textAlign: 'center',
    borderBottom: [1, 'solid'],
    borderColor: ({ borderColor }) => borderColor
  },
  link: {
    margin: [span(1), span(1), span(sliceMarginSpan, 'md')],
    display: 'inline-block',
    textTransform: 'uppercase',
    textDecoration: 'none',
    position: 'relative',
    padding: [vw(8), 0],
    ...theme.link(),
    [theme.breakpoints.up('md')]: {
      padding: [vw(8, 'desktop'), 0],
      margin: '60px 0px 120px 0px'
    }
  },
  lastCard: {
    borderBottom: 'none'
  },
  title: {
    fontSize: vw(28),
    fontWeight: '600',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: vw(18)
    }
  },
  bold: {
    fontWeight: 'bold'
  },
  noResults: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    maxWidth: 'calc(100% - 9.25rem)',
    margin: '20px auto 50px auto',
    '@media (max-width: 1500px)': {
      maxWidth: 'calc(100% - 7rem)'
    },
    '@media (max-width: 1024px)': {
      maxWidth: 'calc(100% - 7rem)'
    },
    '@media (max-width: 600px)': {
      maxWidth: 'calc(100% - 3.5rem)',
      margin: '20px auto 20px auto'
    }
  },
  noResultsTitle: {
    textAlign: 'left',
    fontSize: '24px',
    marginBottom: '10px',
    fontWeight: '600',
    margin: '80px 0px 0px 0px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
      margin: '35px 0px 0px 0px'
    }
  },
  noResultsText: {
    textAlign: 'left',
    fontSize: '16px',
    fontWeight: '500',
    opacity: 0.5,
    margin: '0px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px'
    }
  }
})

export default PageGridSlice
